import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from "@mui/material"
import { People, LocationOn, RateReview, TrendingUp, ArrowForward } from "@mui/icons-material"
import { getDashboardStats } from "../services/adminService"
import { format } from "date-fns"

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h3" component="div" fontWeight="bold" sx={{ mb: 1 }}>
            {value.toLocaleString()}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box display="flex" alignItems="center" mt={1}>
              <TrendingUp fontSize="small" sx={{ mr: 0.5, color: "success.main" }} />
              <Typography variant="body2" color="success.main">
                {trend}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.100`,
            borderRadius: 2,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 40, color: `${color}.main` }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
)

function AdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadDashboardStats()
  }, [])

  const loadDashboardStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getDashboardStats()
      setStats(data)
    } catch (err) {
      console.error("Error loading dashboard stats:", err)
      setError("Failed to load dashboard statistics. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadDashboardStats}>
          Retry
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          📊 Admin Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Overview of TNKL platform statistics
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            subtitle={`+${stats.newUsersWeek} this week`}
            icon={People}
            color="primary"
            trend={`${stats.newUsersToday} today`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bathrooms"
            value={stats.totalBathrooms}
            subtitle={`+${stats.newBathroomsWeek} this week`}
            icon={LocationOn}
            color="success"
            trend={`${stats.newBathroomsToday} today`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Reviews"
            value={stats.totalReviews}
            subtitle={`+${stats.newReviewsWeek} this week`}
            icon={RateReview}
            color="info"
            trend={`${stats.newReviewsToday} today`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Rating"
            value={stats.totalReviews > 0 ? (stats.totalReviews / stats.totalBathrooms).toFixed(1) : "N/A"}
            subtitle="Reviews per bathroom"
            icon={TrendingUp}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Top Contributors */}
      {stats.topContributors && stats.topContributors.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight="bold">
                🏆 Top Contributors
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Contributions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.topContributors.map((contributor, index) => (
                    <TableRow key={contributor.userId}>
                      <TableCell>
                        <Chip label={`#${index + 1}`} size="small" color={index === 0 ? "primary" : "default"} />
                      </TableCell>
                      <TableCell>{contributor.userName}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {contributor.contributions}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity Grid */}
      <Grid container spacing={3}>
        {/* Recent Users */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Signups
                </Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate("/admin/users")}>
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {stats.recentUsers.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {user.displayName || user.email || "Unknown"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.createdAt ? format(user.createdAt, "MMM d, yyyy") : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Bathrooms */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Bathrooms
                </Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate("/admin/bathrooms")}>
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {stats.recentBathrooms.map(bathroom => (
                      <TableRow key={bathroom.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {bathroom.name || bathroom.locationName || "Unnamed"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {bathroom.createdAt ? format(bathroom.createdAt, "MMM d, yyyy") : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Reviews */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Reviews
                </Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate("/admin/activity")}>
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {stats.recentReviews.map((review, index) => (
                      <TableRow key={`${review.bathroomId}-${index}`}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {review.userName || "Anonymous"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {review.bathroomName} - {review.timestamp ? format(review.timestamp, "MMM d") : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default AdminDashboard
